import React, { useState, useEffect, useRef } from 'react';
import { X, Zap, Crown, CloudOff } from 'lucide-react';
// pantallas-alumno (G), PE11-c: la ventana del alumno (velo de la casa y caja de la ventana H del Panel).
import { FONDO_DE_LA_VENTANA } from './panel/VentanaDelPanel';
import { AreaDe44 } from './panel/anatomia';
import { useHojaDeLaCasa, FilaDeAgarradera, ENTRA_EL_VELO, ENTRA_LA_CAJA } from './alumno/arrastreDeHoja';
import api, { logAnalyticsEvent } from '../lib/api';
import { toast } from 'sonner';
import { useReadyTranslation } from '../hooks/useReadyTranslation';
import LegalAcceptance from './LegalAcceptance';
import PackSuccessModal from './PackSuccessModal';
// TANDA AS-3 — en build de app el precio lo pone Apple y el cobro es la hoja nativa.
// En web nada de esto se ejecuta (IS_APP_BUILD es false en runtime).
import { IS_APP_BUILD } from '../lib/buildTarget';
import * as purchases from '../lib/purchases';
import { appleErrorCopy } from '../lib/appleErrorCopy';

// AS-3 (remate del censo §1.5): se fue `perCredit`, que estaba declarado desde siempre y
// no se renderizaba en ninguna parte. El precio por crédito que sí se veía vivía en i18n
// (`pricing:packs.best_value`), en la otra pantalla; ajustes-1-3-0 (Bloque A3, #497) lo
// retiró también de ahí: el chip dice solo "Best value" / "Mejor valor", aquí y allá.
//
// `key` es la clave interna del producto de Apple (lib/purchases.js). La web no la mira.
const PACKS = [
  {
    size: 'small',
    credits: 15,
    price: 4.99,
    key: 'pack_small',
    icon: Zap,
  },
  {
    size: 'large',
    credits: 40,
    price: 8.99,
    key: 'pack_large',
    icon: Crown,
    badgeKey: 'best_value',
  },
];

export default function CreditPackModal({ open, onClose, fromAutoSplit = false }) {
  // El namespace `pricing` (las claves `iap.*`) SÓLO se pide en build de app: sumarlo en
  // web metería una espera nueva a la puerta de `ready` en pantallas donde hoy no se carga.
  const { t, ready, LoadingGate } = useReadyTranslation(
    IS_APP_BUILD ? ['modals', 'common', 'pricing'] : ['modals', 'common']);
  const [loading, setLoading] = useState('');
  // AS-3 — el catálogo de StoreKit, con sus tres estados (F-B). En web queda null.
  const [iap, setIap] = useState(() => (IS_APP_BUILD ? purchases.snapshot() : null));
  /* FESTEJO-PACKS — la palomita de "ya tienes tus créditos" (app). Se enciende
     SÓLO con una acreditación real (ver `comprarConApple`) y en web se queda en
     falso para siempre: la compra por Stripe se festeja en el retorno, desde el
     Panel. */
  const [festejo, setFestejo] = useState(false);
  /* REMATE #209 — DÓNDE EMPEZÓ EL TOQUE. Un ref y no un estado: esto se escribe
     en cada `mousedown`/`touchstart` y no pinta nada, así que un re-render por
     toque sería puro desperdicio. Va aquí arriba porque es un hook y abajo hay
     tres retornos tempranos. Lo lee y lo limpia `cerrarSiEsElFondo`. */
  const arranqueRef = useRef(null);
  useEffect(() => {
    if (!IS_APP_BUILD || !open) return undefined;
    const off = purchases.subscribeProducts(setIap);
    purchases.loadProducts();
    return off;
  }, [open]);
  // marca-1 (#726): la agarradera cierra como la X (bajarla 80 o un tiron) y la hoja entra y sale como las demas.
  // Abierta = lo que de verdad se pinta abajo (el selector, no la palomita del festejo).
  const hoja = useHojaDeLaCasa(onClose, { abierta: !!open && ready && !festejo });

  if (!ready) return <LoadingGate />;
  /* EL FESTEJO OCUPA EL LUGAR DEL SELECTOR, y por eso el `onClose` del anfitrión
     espera hasta que el usuario lo cierre. Visualmente es lo pedido —el modal de
     paquetes se va y aparece la palomita—, pero para el anfitrión la capa NUNCA
     se levantó: su `capaEncima` sigue en alto (así el swipe de pestañas no nace
     debajo de la palomita, doctrina del carril) y el modal de auto-split no
     reaparece detrás del festejo. Al cerrar se hacen las dos cosas, en orden.
     Va ANTES de la guarda de `open` a propósito: si el anfitrión cerrara el modal
     por su cuenta mientras se festeja, la palomita sigue en pie. */
  if (festejo) return <PackSuccessModal onClose={() => { setFestejo(false); onClose(); }} />;
  if (!open) return null;

  const iapStatus = IS_APP_BUILD ? (iap?.status || 'idle') : null;
  const iapReady = IS_APP_BUILD && iapStatus === 'ready';
  const iapFailed = IS_APP_BUILD && iapStatus === 'error';

  /* AS-3 — la compra nativa del consumible: hoja de Apple → JWS a /verify → los créditos
     ya están arriba. El refresco va por el MISMO carril que el retorno de Stripe: al
     confirmar el recibo, lib/purchases.js emite `broadcastPlanChanged()` (invalida el
     screenDataCache y despierta el re-poll del Panel) y llama al refresco de /auth/me que
     el arranque de la app le dejó registrado. Por eso este componente NO estrena
     dependencia del contexto de auth: se monta en tres pantallas y varias suites lo
     renderizan suelto. Cancelar la hoja es silencio absoluto. */
  const comprarConApple = async (packSize) => {
    const pack = PACKS.find(p => p.size === packSize);
    setLoading(packSize);
    logAnalyticsEvent('pack_checkout_started', 'completed',
      { pack: packSize, source: fromAutoSplit ? 'auto_split' : 'credit_modal', provider: 'apple' });
    const res = await purchases.purchase(pack.key);
    setLoading('');
    if (res.status === 'ok') {
      /* FESTEJO-PACKS — SÓLO LA ACREDITACIÓN DE VERDAD FESTEJA. El 200 de
         /payments/apple/verify trae dos desenlaces para un consumible
         (backend/routes/apple_routes.py:497): `credited`, que es "estos créditos
         acaban de subir", y `already_processed`, que es "este recibo ya estaba
         cobrado" — un re-verify de un recibo viejo que Apple volvió a entregar.
         Festejar el segundo sería anunciarle créditos nuevos a alguien que no
         recibió ninguno. */
      if (res.data?.status === 'credited') setFestejo(true);
      else onClose();
    } else if (res.status === 'error') {
      toast.error(appleErrorCopy(res.error, t));
    }
  };

  const handleBuy = async (packSize) => {
    if (IS_APP_BUILD) { await comprarConApple(packSize); return; }
    setLoading(packSize);
    // Purchase instrumentation: fire-and-forget intent event BEFORE the checkout call so it
    // lands ahead of the Stripe redirect (a full create-pack-checkout round-trip separates them).
    logAnalyticsEvent('pack_checkout_started', 'completed', { pack: packSize, source: fromAutoSplit ? 'auto_split' : 'credit_modal' });
    try {
      const body = {
        pack_size: packSize,
        origin_url: window.location.origin,
      };
      // When opened from AutoSplitModal, propagate the flag so the Stripe success_url
      // includes &from_auto_split=true and Dashboard's intent-detection effect can
      // restore the modal on return.
      if (fromAutoSplit) body.from_auto_split = true;
      const res = await api.post('/stripe/create-pack-checkout', body);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || t('modals:credit_pack.toasts.checkout_failed'));
    } finally {
      setLoading('');
    }
  };

  /* TANDA 18 (#209) — TOCAR EL FONDO ES EXACTAMENTE EL BOTÓN DE CERRAR.
     La única salida era la X de la esquina, y un modal que no se cierra por
     donde el dedo lo intenta se siente atorado. Es equivalencia pura: la misma
     función del anfitrión y las mismas condiciones —hoy la X no tiene ninguna,
     ni con una compra en vuelo, así que el fondo tampoco inventa una—.

     Se compara el BLANCO del click en vez de detener la propagación adentro: la
     tarjeta ya lleva los dos botones de compra, el de cerrar y el de reintentar
     el catálogo, y pincharle un `stopPropagation` al contenedor metería una
     regla nueva en el camino de todos ésos. Comparar el blanco no toca a nadie,
     y por eso la X que burbujea hasta aquí no puede cerrar dos veces.

     La lógica de compra no se rozó.

     ── REMATE — Y EL RESBALÓN NO CUENTA ────────────────────────────────────
     Comparar el blanco del `click` no alcanzaba solo. Si el dedo APRIETA dentro
     de la tarjeta y SUELTA sobre el fondo —arrastrar para seleccionar el
     renglón honesto, o resbalarse al apuntar a un paquete—, el navegador manda
     el `click` al ancestro común, que es el fondo: el blanco decía "fondo" y el
     modal se cerraba con el usuario a media elección.

     La regla completa: el fondo cierra sólo si el toque EMPEZÓ en el fondo. El
     arranque se recuerda con `mousedown` y `touchstart` —los dos eventos que la
     casa ya usa, y los únicos que jsdom sintetiza de verdad, así que la regla se
     puede fijar en rojo— y se limpia en cuanto el click decide, porque el ref es
     memoria de UN gesto: dejarlo pegado mataría la salida hasta recargar.

     SIN ARRANQUE REGISTRADO SE CIERRA IGUAL, y es deliberado: un `click` que
     llega sin su `mousedown` es sintético (un lector de pantalla, una
     automatización, el teclado). Una SALIDA tiene que fallar del lado de dejar
     salir. */
  const marcarArranque = (e) => { arranqueRef.current = e.target; };
  const cerrarSiEsElFondo = (e) => {
    const arranque = arranqueRef.current;
    arranqueRef.current = null;
    if (e.target !== e.currentTarget) return;          // el click nació adentro
    if (arranque && arranque !== e.currentTarget) return;   // el toque, también
    onClose();
  };

  /* pantallas-alumno (G), PE11-c y PE12-a: LA VENTANA DEL ALUMNO, 480, la MISMA venga de donde venga (Pendientes o
     el Panel). El velo de la casa (fijo en los cuatro bordes, negro al 66 %, z 70, sobre la barra de arriba) y la caja
     de la ventana H (#161616, borde #2A2A2A; en el celular hoja desde abajo con agarradera). Su X de hoy (con area de
     44), el contenido de hoy, y los dos paquetes con el naranja de la casa en renglones del Panel (hoy morado y
     ambar). La regla del toque en el fondo (TANDA 18 y su remate) no cambia. */
  return (
    <div ref={hoja.veloRef} className={`${FONDO_DE_LA_VENTANA} ${ENTRA_EL_VELO}`} data-testid="credit-pack-modal"
      onMouseDown={marcarArranque} onTouchStart={marcarArranque} onClick={cerrarSiEsElFondo}>
      <div ref={hoja.cajaRef} role="dialog" aria-modal="true" aria-labelledby="credit-pack-title"
        className={`relative w-full md:max-w-[480px] max-h-[90dvh] overflow-y-auto bg-[#161616] border border-[#2A2A2A] rounded-t-[24px] md:rounded-[20px] shadow-2xl px-6 pt-4 md:pt-6 pb-[max(1.5rem,env(safe-area-inset-bottom))] md:pb-6 max-md:overscroll-contain max-md:touch-pan-y ${ENTRA_LA_CAJA}`}
        data-testid="credit-pack-panel">
        <FilaDeAgarradera agarradera={hoja.agarradera} testid="credit-pack-agarradera"
          clase="md:hidden relative flex justify-center pb-2" />
        <button
          type="button"
          onClick={onClose}
          aria-label={t('common:close')}
          className="absolute z-[2] top-2.5 right-3 md:top-4 md:right-4 w-10 h-10 rounded-xl flex items-center justify-center text-[#9A9A9A] hoverable:text-white hoverable:bg-[#1B1B1B] focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          data-testid="credit-pack-close"
        >
          <AreaDe44 />
          <X className="w-5 h-5" />
        </button>

        {/* R-10 (censo de X): el titulo deja aire para la X (40 de relleno a la derecha). */}
        <h2 id="credit-pack-title" className="text-[17px] font-semibold text-[#F4F4F3] pr-10">{t('modals:credit_pack.title')}</h2>
        <p className="text-[14px] text-[#9A9A9A] mt-1 mb-5">{t('modals:credit_pack.subtitle')}</p>

        {/* AS-3 (F-B) - el FALLO del catálogo de StoreKit. App-only: en web la rama no se cumple.
            TANDA 16 (#199): el renglón de "cargando precios" se fue (cada precio reserva su hueco con su
            esqueleto). El aviso de ERROR se queda, con su reintento: un renglon con nube tachada. */}
        {iapFailed && (
          <div className="mb-3 flex items-center gap-3 rounded-[14px] border border-[#262626] bg-[#101010] px-3.5 py-3" data-testid="iap-error">
            <CloudOff className="w-4 h-4 shrink-0 text-[#9A9A9A]" aria-hidden="true" />
            <p className="flex-1 min-w-0 text-[13px] leading-[18px] text-[#C8C8C8]">{t('pricing:iap.load_failed')}</p>
            <button
              type="button"
              onClick={() => purchases.loadProducts()}
              className="shrink-0 px-4 rounded-xl text-[14px] font-semibold bg-[#1B1B1B] border border-[#2C2C2C] text-[#F4F4F3]"
              style={{ minHeight: 44 }}
              data-testid="iap-retry"
            >
              {t('pricing:iap.retry')}
            </button>
          </div>
        )}

        <div className="flex flex-col gap-2.5">
          {PACKS.map((pack) => {
            const Icon = pack.icon;
            const packProducto = IS_APP_BUILD ? (iap?.products || {})[pack.key] : null;
            // WEB: la constante local de siempre. APP: el precio localizado por Apple, y
            // JAMÁS el de la web como respaldo (sin producto no se pinta cifra).
            const packPrecio = IS_APP_BUILD
              ? ((iapReady && packProducto) ? packProducto.priceString : null)
              : `$${pack.price.toFixed(2)}`;
            // remate ajustes-1-3-0 (R1, #508): en la app, la divisa del producto de StoreKit
            // junto al precio, EXACTAMENTE como la pagina de subir de plan. En la web no se pinta.
            const packMoneda = (IS_APP_BUILD && iapReady && packProducto)
              ? packProducto.currencyCode : 'USD';
            return (
              <button
                key={pack.size}
                onClick={() => handleBuy(pack.size)}
                disabled={!!loading || (IS_APP_BUILD && !iapReady)}
                className="flex items-center justify-between gap-3 min-h-[60px] px-3.5 py-3 rounded-[14px] border border-[#262626] bg-[#101010] hoverable:border-[#3A2414] transition-colors w-full text-left cursor-pointer disabled:cursor-default"
                data-testid={`pack-option-${pack.size}`}
              >
                {/* Layout-only (R2): badge sits on its OWN line under the pack name. */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <div data-parte="icono" className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0 bg-[#1C130D]">
                    <Icon className="w-5 h-5 text-[#F27A2E]" />
                  </div>
                  <div className="min-w-0">
                    <span className="text-[15px] font-semibold text-[#F4F4F3] block">
                      {t('modals:credit_pack.packs.credits_label', { count: pack.credits })}
                    </span>
                    {pack.badgeKey && (
                      <span
                        className="inline-block mt-1 text-[12px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border border-[#3A2414] bg-[#241710] text-[#F7A06A]"
                        data-testid={`pack-badge-${pack.size}`}
                      >
                        {t(`modals:credit_pack.packs.badges.${pack.badgeKey}`)}
                      </span>
                    )}
                  </div>
                </div>
                {packPrecio === null && loading !== pack.size ? (
                  /* AS-3 (F-B): esqueleto en el hueco del precio, SIN cifra. */
                  <span
                    className="rounded-full shrink-0 ml-3"
                    style={{ display: 'inline-block', width: 72, height: 32, background: '#262626' }}
                    aria-label={t('pricing:iap.loading')}
                    data-testid={`pack-price-skeleton-${pack.size}`}
                  />
                ) : (
                  <span
                    className={`px-3 py-1.5 rounded-full text-[13px] font-semibold shrink-0 ml-3 border ${loading === pack.size ? 'bg-[#262626] border-[#2C2C2C] text-[#6A6A6A]' : 'bg-[#1B1B1B] border-[#2C2C2C] text-[#F4F4F3]'}`}
                    data-testid={`buy-pack-${pack.size}`}
                  >
                    {loading === pack.size ? t('common:loading') : IS_APP_BUILD
                      ? <>{packPrecio} <span style={{ fontSize: 11, color: '#666' }} data-testid={`pack-currency-${pack.size}`}>{packMoneda}</span></>
                      : packPrecio}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* ajustes-1-3-0 (Bloque G, #498) y su remate (R1, #508): "Prices in USD", SOLO en la web. */}
        {!IS_APP_BUILD && (
          <p className="text-[13px] text-[#9A9A9A] mt-3 text-center" data-testid="credit-pack-moneda">
            {t('modals:credit_pack.prices_in_usd')}
          </p>
        )}

        {/* Medicion (PE11-c, PE12): las ligas legales median 16 de alto; un relleno vertical en linea no mueve el renglon
            y el toque llega a 44. La pieza es compartida: se le pide por su clase. */}
        <LegalAcceptance variant="purchase" className="mt-4 [&_a]:py-[14px]" />

        <p className="text-[13px] text-[#9A9A9A] mt-4 text-center leading-relaxed" data-testid="credit-pack-honest-line">
          {t('modals:credit_pack.honest_line')}
        </p>
      </div>
    </div>
  );
}
